/**
 * TodoBottomSheet.js
 * 모바일 To-Do 추가/수정용 Bottom Sheet 컴포넌트
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Slide,
  Backdrop,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import { X, Search } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const TodoBottomSheet = ({
  open,
  onClose,
  onSubmit,
  initialData = null,
  companies = [],
  contracts = [],
  title = 'To-Do 리스트 추가',
}) => {
  const { colors, isDark } = useCustomTheme();
  const inputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    work_description: '', // 업무 상세내용 (note_1에 저장)
    note_1: '', // 비고 (note_2에 저장)
    contract: null, // 계약 객체 (업체명-계약명칭 포함)
    job_category: '',
  });

  // 계약 검색 관련 상태
  const [contractOptions, setContractOptions] = useState([]);
  const [recentContracts, setRecentContracts] = useState([]);
  const [isSearchingContracts, setIsSearchingContracts] = useState(false);
  const [contractSearchQuery, setContractSearchQuery] = useState('');

  // 계약 검색 함수
  const searchContracts = useCallback(async (query) => {
    if (!query || query.length < 2) return;
    
    setIsSearchingContracts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/search`, { params: { q: query } });
      const searched = (response.data || []).map(c => ({
        ...c,
        display_label: `${c.company_name_kr || ''} - ${c.contract_title || c.contract_id || ''}`
      }));
      setContractOptions(searched);
    } catch (error) {
      console.error('계약 검색 실패:', error);
      setContractOptions([]);
    } finally {
      setIsSearchingContracts(false);
    }
  }, []);

  // 최근 사용 계약 조회
  useEffect(() => {
    const fetchRecentContracts = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/contracts/recent`, { params: { limit: 5 } });
        const recent = (response.data || []).map(c => ({
          ...c,
          isRecent: true,
          display_label: `${c.company_name_kr || ''} - ${c.contract_title || c.contract_id || ''}`
        }));
        setRecentContracts(recent);
      } catch (error) {
        console.error('최근 계약 조회 실패:', error);
      }
    };

    if (open) {
      fetchRecentContracts();
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
    }
  }, [open]);

  // 계약 검색 (디바운싱)
  useEffect(() => {
    if (!contractSearchQuery || contractSearchQuery.length < 2) {
      setContractOptions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchContracts(contractSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [contractSearchQuery, searchContracts]);

  useEffect(() => {
    if (initialData) {
      // 수정 모드: 기존 데이터 채우기
      let contract = null;
      if (initialData.contract_id) {
        contract = contracts.find(c => c.contract_id === initialData.contract_id);
        if (contract) {
          // 업체 정보 찾기
          const company = companies.find(c => c.company_code === contract.company_code || c.company_code === initialData.company_code);
          // 계약 객체에 company_name_kr과 display_label 추가
          contract = {
            ...contract,
            company_name_kr: company?.company_name_kr || contract.company_code || initialData.company_code || '',
            display_label: `${company?.company_name_kr || contract.company_code || initialData.company_code || ''} - ${contract.contract_title || contract.contract_id || ''}`,
          };
        } else if (initialData.company_code && initialData.contract_id) {
          // 계약을 찾지 못한 경우, company_code와 contract_id로 계약 객체 생성
          const company = companies.find(c => c.company_code === initialData.company_code);
          contract = {
            contract_id: initialData.contract_id,
            company_code: initialData.company_code,
            company_name_kr: company?.company_name_kr || initialData.company_code,
            contract_title: initialData.contract_id,
            display_label: `${company?.company_name_kr || initialData.company_code} - ${initialData.contract_id}`,
          };
        }
      }
      
      setFormData({
        work_description: initialData.note_1 || '', // 업무 상세내용
        note_1: initialData.note_2 || '', // 비고
        contract: contract,
        job_category: initialData.job_category || '',
      });
    } else {
      // 추가 모드: 초기화
      setFormData({
        work_description: '',
        note_1: '',
        contract: null,
        job_category: '',
      });
      setContractSearchQuery('');
      setContractOptions([]);
    }
  }, [initialData, open, companies, contracts]);

  const handleSubmit = () => {
    if (!formData.work_description.trim()) {
      alert('업무내용을 입력해주세요.');
      return;
    }

    // 계약 정보에서 company_code와 contract_id 추출
    const companyCode = formData.contract?.company_code || null;
    const contractId = formData.contract?.contract_id || null;
    
    const submitData = {
      company_code: companyCode,
      contract_id: contractId,
      note_1: formData.work_description.trim(),  // 업무 상세내용을 note_1에 저장
      note_2: formData.note_1.trim() || null,  // 비고를 note_2에 저장
      job_category: formData.job_category?.trim() || null,  // 업무 구분 (선택)
    };

    onSubmit(submitData);
    onClose();
  };

  return (
    <>
      <Backdrop
        open={open}
        onClick={onClose}
        sx={{
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      />
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1301,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* 헤더 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              borderBottom: `1px solid ${colors.primaryBorder}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: colors.primaryText,
                fontWeight: 600,
                fontSize: '16px',
              }}
            >
              {initialData ? 'To-Do 수정' : 'To-Do 리스트 추가'}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                minWidth: '48px',
                minHeight: '48px',
                color: colors.secondaryText,
              }}
            >
              <X size={24} />
            </IconButton>
          </Box>

          {/* 내용 */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch', // iOS 부드러운 스크롤
              touchAction: 'pan-y', // 수직 스크롤만 허용
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            {/* 1. 업체/계약 검색 (선택) - PC 버전과 동일한 통합 검색 */}
            <Box sx={{ mb: 2 }}> {/* 검색 필드 하단 여백 추가 */}
              <Autocomplete
                options={[
                  ...(recentContracts.map(c => ({ ...c, isRecent: true }))),
                  ...(recentContracts.length > 0 && contractOptions.length > 0 ? [{ divider: true }] : []),
                  ...(contractOptions.map(c => ({ ...c })))
                ]}
                getOptionLabel={(option) => {
                  if (option.divider) return '';
                  return option.display_label || `${option.company_name_kr || ''} - ${option.contract_title || option.contract_id || ''}`;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (option.divider) return false;
                  return option.contract_id === value?.contract_id;
                }}
                value={formData.contract}
                onChange={(_, newValue) => {
                  if (newValue && !newValue.divider) {
                    setFormData(prev => ({ ...prev, contract: newValue }));
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  setContractSearchQuery(newInputValue);
                  if (newInputValue.length >= 2) {
                    searchContracts(newInputValue);
                  }
                }}
                loading={isSearchingContracts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="업체/계약 검색"
                    placeholder="업체명 또는 계약명을 입력하세요"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search size={18} style={{ marginRight: 8, color: colors.secondaryText }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: '16px',
                        minHeight: '56px',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  if (option.divider) {
                    return <Divider key="divider" />;
                  }
                  return (
                    <ListItem {...props} key={option.contract_id}>
                      {option.isRecent && (
                        <Chip
                          label="최근 사용"
                          size="small"
                          sx={{ mr: 1, height: 20, fontSize: '0.7rem' }}
                          color="primary"
                        />
                      )}
                      <ListItemText
                        primary={option.display_label || `${option.company_name_kr || ''} - ${option.contract_title || option.contract_id || ''}`}
                        secondary={option.contract_id}
                      />
                    </ListItem>
                  );
                }}
                noOptionsText="2자 이상 입력하세요"
              />
            </Box>

            {/* 2. 업무 구분 */}
            <Box>
              <TextField
                label="업무 구분"
                value={formData.job_category || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, job_category: e.target.value }))}
                placeholder="예: 정산, 정정, 보고서 (자유 입력 가능)"
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '16px',
                    minHeight: '56px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* 3. 업무 상세내용 입력 (필수) */}
            <TextField
              inputRef={inputRef}
              label="업무 상세내용"
              placeholder="여기에 업무내용 상세하게 기재 해주세요 (Ctrl+Enter로 저장)"
              value={formData.work_description}
              onChange={(e) => setFormData(prev => ({ ...prev, work_description: e.target.value }))}
              multiline
              minRows={3}
              maxRows={10}
              fullWidth
              required
              autoFocus
              onKeyDown={(e) => {
                // Ctrl+Enter 또는 Cmd+Enter로 저장
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit();
                }
              }}
              sx={{
                mt: 0, // Box의 mb와 함께 사용하므로 mt는 0으로 설정
                '& .MuiInputBase-root': {
                  fontSize: '16px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '14px',
                },
                '& .MuiInputBase-input': {
                  overflow: 'auto',
                  resize: 'vertical', // 수직으로만 크기 조절 가능
                },
              }}
            />

            {/* 4. 비고 입력 (선택) */}
            <TextField
              label="비고"
              placeholder="업무 기한 등 참고사항을 입력하세요 (Ctrl+Enter로 저장)"
              value={formData.note_1}
              onChange={(e) => setFormData(prev => ({ ...prev, note_1: e.target.value }))}
              multiline
              minRows={1}
              maxRows={5}
              fullWidth
              onKeyDown={(e) => {
                // Ctrl+Enter 또는 Cmd+Enter로 저장
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit();
                }
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '16px',
                  minHeight: '28px', // 기존 56px의 50% 수준
                },
                '& .MuiInputLabel-root': {
                  fontSize: '14px',
                },
                '& .MuiInputBase-input': {
                  overflow: 'auto',
                  resize: 'vertical', // 수직으로만 크기 조절 가능
                },
              }}
            />
          </Box>

          {/* 하단 버튼 */}
          <Box
            sx={{
              p: 3,
              borderTop: `1px solid ${colors.primaryBorder}`,
              display: 'flex',
              gap: 2,
            }}
          >
            <Button
              type="button"
              onClick={onClose}
              variant="outlined"
              sx={{
                flex: 3,
                minHeight: '52px',
                fontSize: '12px',
                fontWeight: 600,
                borderColor: colors.primaryBorder,
                color: colors.primaryText,
              }}
            >
              취소
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                flex: 7,
                minHeight: '52px',
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: colors.accent,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: colors.accent,
                  opacity: 0.9,
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              {initialData ? '변경 사항 저장' : '저장'}
            </Button>
          </Box>
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

export default TodoBottomSheet;

